import React from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  CHECKOUT_METHOD_ORDER_ONLY,
  ORDER_TYPE_DELIVERY,
  ORDER_TYPE_INHOUSE,
  ORDER_TYPE_PICKUP,
  ORDER_TYPE_SELF_SERVICE,
} from '../../../../utility/constants';
import { setCheckout } from '../../../../redux/store/store.actions';
import {
  getCheckoutMethod,
  getActiveOrderConfig,
  getCheckoutMethodFromConfig,
  getServiceFeeFromConfig,
  getPosConflictCartItem,
  getLocalizedName,
} from '../../../../utility/util/util.service';
import OptionsList from '../options-list/options-list.component';

const SelectOrderType = ({
  store,
  checkout,
  availableOrderTypes,
  table,
  kiosk,
  cart,
  setCheckout,
}) => {
  const intl = useIntl();

  // An order type is disabled when its order config has a POS integration but
  // the cart contains an item/addition without a posIntegrationRef (it cannot be
  // synced to that POS). Returns the conflicting cart item, or null if available.
  const getPosConflictItem = (orderType) => {
    const config = getActiveOrderConfig(store, orderType, table, kiosk);
    return getPosConflictCartItem(config, cart);
  };

  // Get service fees from order configs
  const getServiceFee = (orderType) => {
    const config = getActiveOrderConfig(store, orderType, table, kiosk);
    return getServiceFeeFromConfig(config);
  };

  // Get checkout method from order config
  // Area configuration has higher priority than order-config
  const getConfigCheckoutMethod = (orderType) => {
    const config = getActiveOrderConfig(store, orderType, table, kiosk);
    if (config) {
      return getCheckoutMethodFromConfig(config, table?.area, {
        table,
        store,
        orderType,
      });
    }
    // Fallback to legacy merchantProfile
    return getCheckoutMethod(orderType, store, table?.area, table);
  };

  // Filter order types for kiosk without terminal (only order_only methods allowed)
  const filteredOrderTypes =
    kiosk && !kiosk.terminal
      ? availableOrderTypes?.filter((orderType) => {
          const checkoutMethod = getConfigCheckoutMethod(orderType);
          return checkoutMethod === CHECKOUT_METHOD_ORDER_ONLY;
        })
      : availableOrderTypes;

  return (
    <>
      {availableOrderTypes && (
        <>
          {availableOrderTypes?.length === 1 &&
          availableOrderTypes[0] === ORDER_TYPE_INHOUSE ? (
            <span className="font-small-3 mb-1 text-primary">
              <FormattedMessage
                id="cartInhouseOrderTypeText"
                values={{ tableName: table?.name }}
              />
            </span>
          ) : (
            <>
              <span className="font-small-3 text-primary">
                <FormattedMessage id="cartOrderTypeTitle" />
              </span>
              <OptionsList
                isMulti={false}
                options={filteredOrderTypes}
                values={checkout?.orderType && [checkout.orderType]}
                getOptionValue={(type) =>
                  intl.formatMessage({
                    id: `orderType${type}${kiosk ? 'Kiosk' : ''}`,
                  })
                }
                getOptionSuffix={(type) => {
                  const serviceFee = getServiceFee(type);
                  return serviceFee > 0
                    ? '+' +
                      intl.formatNumber(serviceFee, {
                        format: 'priceCurrency',
                      })
                    : null;
                }}
                getOptionDisabled={(type) => !!getPosConflictItem(type)}
                getOptionDisabledReason={(type) => {
                  const conflictItem = getPosConflictItem(type);
                  if (!conflictItem) return null;
                  return intl.formatMessage(
                    { id: 'orderTypeUnavailableForCartItems' },
                    {
                      itemName: getLocalizedName(
                        conflictItem.itemData,
                        intl.locale,
                      ),
                      orderType: intl.formatMessage({
                        id: `orderType${type}${kiosk ? 'Kiosk' : ''}`,
                      }),
                    },
                  );
                }}
                onChange={(orderType) => {
                  if (getPosConflictItem(orderType)) return;
                  if (orderType !== checkout.orderType) {
                    setCheckout({
                      orderType,
                      checkoutMethod: getConfigCheckoutMethod(orderType),
                    });
                  }
                }}
                required
                optionRenderer={(option) => (
                  <div data-testid={`option-${option.value}`}>
                    {/* Render your option content here */}
                    {option.label}
                  </div>
                )}
              />
            </>
          )}
        </>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  store: state.store.store,
  table: state.store.table,
  checkout: state.store.checkout,
  kiosk: state.store.kiosk,
  cart: state.store.cart,
  availableOrderTypes: state.store.availableOrderTypes,
});

export default connect(mapStateToProps, {
  setCheckout,
})(SelectOrderType);
